import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { Toaster } from 'react-hot-toast';
import { motion } from 'framer-motion';
import { useAnalytics } from './hooks/useAnalytics';

// Pages
import HomePage from './pages/HomePage';
import MyGamesPage from './pages/MyGamesPage';
import CategoriesPage from './pages/CategoriesPage';
import GameDetailPage from './pages/GameDetailPage';
import PlayPage from './pages/PlayPage';
import StreamConnectorPage from './pages/StreamConnectorPage';
import SearchPage from './pages/SearchPage';
import AuthPage from './pages/AuthPage';
import ProfilePage from './pages/ProfilePage';

// Components
import Layout from './components/layout/Layout';
import ProtectedRoute from './components/auth/ProtectedRoute';

// Services
import authService from './services/authService';
import { analyticsService } from './services/analyticsService';
import { useThemeStore } from './store/useThemeStore';
import { useGamesStore } from './store/useGamesStore';
import { useWebplaySession } from './hooks/useWebplaySession';
import { StreamingLimitsProvider } from './contexts/StreamingLimitsContext';

// Initialize theme
const initializeTheme = () => {
  const { isDark } = useThemeStore.getState();
  if (isDark) {
    document.documentElement.classList.add('dark');
  } else {
    document.documentElement.classList.remove('dark');
  }
};

// Initialize theme immediately
initializeTheme();

function AppRouter() {
  const location = useLocation();
  const { trackPageView } = useAnalytics();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState<any>(null);
  
  // Get reset function from games store
  const resetGamesStore = useGamesStore(state => state.reset);
  
  // Initialize session tracking for entire app
  useWebplaySession();

  // Initialize analytics on mount
  useEffect(() => {
    const apiKey = import.meta.env.VITE_AMPLITUDE_API_KEY;
    analyticsService.initialize({ amplitudeApiKey: apiKey });
  }, []);

  // Track page views on route changes
  useEffect(() => {
    if (isAuthenticated && !isLoading) {
      const pageName = location.pathname === '/' ? 'home' : 
                       location.pathname.replace('/', '').replace(/\//g, '_');
      trackPageView(pageName, {
        is_authenticated: isAuthenticated
      });
    }
  }, [location.pathname, isAuthenticated, isLoading, trackPageView]);

  useEffect(() => {
    const checkAuth = async () => {
      console.log('🔍 Checking authentication status...');
      
      try {
        if (authService.isAuthenticated()) {
          console.log('🎫 Found existing tokens, verifying...');
          
          try {
            const userData = await authService.getCurrentUser();
            console.log('👤 Current user:', userData);
            const userObj = userData.user || userData;
            setUser(userObj);
            setIsAuthenticated(true);
            
            // Identify user in analytics
            if (userObj?.id) {
              analyticsService.identify(userObj.id.toString(), {
                user_id: userObj.id.toString(),
                username: userObj.username,
                display_name: userObj.displayName || userObj.username,
                email: userObj.email
              });
            }
          } catch (userError) {
            console.error('❌ Failed to fetch user:', userError);
            const errorMessage = (userError as any)?.message || '';
            if (errorMessage.includes('401') || errorMessage.includes('Unauthorized')) {
              authService.clearTokens();
            }
            setIsAuthenticated(false);
            setUser(null);
          }
        } else {
          console.log('❌ No tokens found');
          setIsAuthenticated(false);
          setUser(null);
        }
      } catch (error) {
        console.error('❌ Auth check failed:', error);
        authService.clearTokens();
        setIsAuthenticated(false);
      } finally {
        setIsLoading(false);
      }
    };

    checkAuth();
  }, []);

  const login = async () => {
    console.log('🔐 User logged in successfully');
    try {
      const userData = await authService.getCurrentUser();
      console.log('👤 User data after login:', userData);
      const userObj = userData.user || userData;
      setUser(userObj);
      setIsAuthenticated(true);
      
      // Identify user in analytics
      if (userObj?.id) {
        analyticsService.identify(userObj.id.toString(), {
          user_id: userObj.id.toString(),
          username: userObj.username,
          display_name: userObj.displayName || userObj.username,
          email: userObj.email
        });
      }
    } catch (error) {
      console.error('❌ Failed to fetch user after login:', error);
      setIsAuthenticated(true); // Still mark as authenticated
    }
  };

  const logout = () => {
    console.log('🚪 User logging out');
    authService.logout();
    analyticsService.reset();
    resetGamesStore();
    setIsAuthenticated(false);
    setUser(null);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-dark-bg flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-dark-text-muted">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-dark-bg">
      <Routes>
        {/* Public Auth Route */}
        <Route
          path="/login"
          element={
            isAuthenticated ? (
              <Navigate to="/" replace />
            ) : (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
              >
                <AuthPage onLogin={login} />
              </motion.div>
            )
          }
        />

        {/* Full-Screen Play Route (no layout) */}
        <Route
          path="/play/:gameId"
          element={
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.3 }}
            >
              <PlayPage />
            </motion.div>
          }
        />

        {/* Studio Preview Route — opened as popup from beemi-studio editor, reuses PlayPage with preview mode */}
        <Route
          path="/studio-preview/:gameId"
          element={
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.3 }}
            >
              <PlayPage />
            </motion.div>
          }
        />

        {/* Stream Connector Popup Route (no layout) */}
        <Route
          path="/stream-connector"
          element={
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.3 }}
            >
              <StreamConnectorPage />
            </motion.div>
          }
        />

        {/* Protected Routes with Layout */}
        <Route
          path="/*"
          element={
            <ProtectedRoute isAuthenticated={isAuthenticated}>
              <Layout user={user} onLogout={logout}>
                <Routes>
                  <Route path="/" element={<HomePage />} />
                  <Route path="/my-games" element={<MyGamesPage />} />
                  <Route path="/categories" element={<CategoriesPage />} />
                  <Route path="/games/:gameId" element={<GameDetailPage />} />
                  <Route path="/search" element={<SearchPage />} />
                  <Route path="/profile" element={<ProfilePage user={user} onLogout={logout} />} />
                </Routes>
              </Layout>
            </ProtectedRoute>
          }
        />
      </Routes>

      {/* Toast Notifications */}
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 4000,
          style: {
            background: 'var(--toast-bg, #fff)',
            color: 'var(--toast-color, #333)',
            border: '1px solid var(--toast-border, #e5e7eb)',
          },
          success: {
            duration: 3000,
            iconTheme: {
              primary: '#10b981',
              secondary: '#fff',
            },
          },
          error: {
            duration: 5000,
            iconTheme: {
              primary: '#ef4444',
              secondary: '#fff',
            },
          },
        }}
      />
    </div>
  );
}

function App() {
  return (
    <Router>
      <StreamingLimitsProvider>
        <AppRouter />
      </StreamingLimitsProvider>
    </Router>
  );
}

export default App;

